import { Box, Button, Divider, Paper, Stack, Typography } from '@mui/material';
import { IconMicrophone, IconPlayerPlay, IconSettings, IconWaveSine } from '@tabler/icons-react';
import type { WorkspaceModule } from '@shared/designVariants/catalog';

/** Reference-specific composition over the current agent's native operations. */
export const AgentStudioPanels = ({
  threeColumns,
  voice,
  language,
  model,
  transcriber,
  canTest,
  openTest,
  configure,
  resource,
  workflow,
  campaign,
}: {
  threeColumns: boolean;
  voice: string;
  language: string;
  model: string;
  transcriber: string;
  canTest: boolean;
  openTest: () => void;
  configure: () => void;
  resource: (module: WorkspaceModule) => void;
  workflow: () => void;
  campaign: () => void;
}) => (
  <>
    {threeColumns && (
      <Paper
        component="aside"
        aria-label="Agent configuration"
        variant="outlined"
        sx={{ minWidth: 0, alignSelf: 'start' }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ p: 2 }}>
          Configuration
        </Typography>
        <Divider />
        <Stack divider={<Divider />}>
          {[
            ['Task flow', workflow],
            ['Tools', () => resource('Tools')],
            ['Knowledge base', () => resource('Knowledge base')],
            ['Voice & speech', configure],
            ['Transcription', configure],
            ['Conversation timing', campaign],
            ['Call settings & routing', campaign],
            ['Post-call analysis', () => resource('Evaluations')],
            ['WhatsApp', () => resource('WhatsApp')],
            ['Webhooks', () => resource('Webhooks')],
          ].map(([label, action]) => (
            <Button
              key={String(label)}
              onClick={action as () => void}
              sx={{ justifyContent: 'start', p: 2, color: 'text.primary', textAlign: 'start' }}
              startIcon={<IconSettings size={17} />}>
              {String(label)}
            </Button>
          ))}
        </Stack>
      </Paper>
    )}
    <Stack
      component="aside"
      aria-label="Agent testing and conversation settings"
      gap={2}
      sx={{ minWidth: 0, alignSelf: 'start' }}>
      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Stack gap={2}>
          <Typography variant="subtitle1" fontWeight={600}>
            Test your agent
          </Typography>
          <Stack alignItems="center" gap={2} sx={{ py: 3 }}>
            <Box
              sx={{
                p: 2,
                border: 1,
                borderColor: 'divider',
                borderRadius: '50%',
                display: 'flex',
              }}>
              <IconMicrophone size={30} />
            </Box>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Review browser, phone or WebSocket test settings before connecting a real call.
            </Typography>
            <Button
              variant="contained"
              fullWidth
              startIcon={<IconPlayerPlay size={16} />}
              disabled={!canTest}
              onClick={openTest}>
              Open test setup
            </Button>
            <Typography variant="caption" color="text.secondary">
              Opening setup does not place a call.
            </Typography>
          </Stack>
        </Stack>
      </Paper>
      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Stack gap={2}>
          <Typography variant="subtitle1" fontWeight={600}>
            Conversation settings
          </Typography>
          {Object.entries({
            Voice: voice,
            Language: language,
            Model: model,
            Transcriber: transcriber,
          }).map(([label, value]) => (
            <Box key={label}>
              <Typography variant="caption" color="text.secondary">
                {label}
              </Typography>
              <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                {value}
              </Typography>
            </Box>
          ))}
          <Divider />
          <Button startIcon={<IconWaveSine size={17} />} onClick={configure}>
            Configure agent
          </Button>
          <Button onClick={campaign}>Open campaign settings</Button>
        </Stack>
      </Paper>
    </Stack>
  </>
);
